import { useEffect, useId, useState } from 'react';
import { IconChevronDown, IconChevronRight, IconMinus, IconPlus } from '@tabler/icons-react';
import type { DateQuestionRules, DateRuleBound, DateTimeQuestionConfig, FormField } from '../../types';
import { AnimatedLabel } from '../ui/AnimatedLabel';
import { AnimatedPresence } from '../ui/AnimatedPresence';

const control = 'min-h-10 w-full min-w-0 max-w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-volt';
const action = 'inline-flex min-h-10 cursor-pointer items-center justify-center rounded-lg px-3 text-sm text-muted-foreground hover:bg-surface-hover focus-visible:outline-2 focus-visible:outline-volt disabled:opacity-40';
const weekdays = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

/** Preserve a cleared input or partial minus sign while the creator types an offset. */
function DayOffset({ label, value, onChange }: { label: string; value: number; onChange: (value: number) => void }) {
  const [draft, setDraft] = useState(String(value));
  useEffect(() => setDraft(String(value)), [value]);
  return <input type="number" aria-label={label} min={-36500} max={36500} step={1} value={draft} className={`${control} tabular-nums`}
    onChange={event => {
      const next = event.target.value;
      setDraft(next);
      if (next !== '') onChange(Number(next));
    }} onBlur={() => { if (draft === '') setDraft(String(value)); }} />;
}

const calendarDayMs = 86_400_000;
const blockedDateFormat = new Intl.DateTimeFormat('en-GB', { timeZone: 'UTC', day: 'numeric', month: 'short', year: 'numeric' });

/** Parse calendar days in UTC so range expansion cannot skip a day during a clock change. */
function calendarDay(value: string): number {
  const stamp = Date.parse(`${value}T00:00:00Z`);
  return value.length === 10 && value >= '0001-01-01' && Number.isFinite(stamp)
    && new Date(stamp).toISOString().slice(0, 10) === value ? stamp : NaN;
}

/** Group adjacent saved dates into compact ranges without changing the stored contract. */
function blockedRanges(dates: string[]): { from: string; to: string }[] {
  return [...new Set(dates)].sort().reduce<{ from: string; to: string }[]>((ranges, day) => {
    const last = ranges.at(-1);
    if (last && calendarDay(day) - calendarDay(last.to) === calendarDayMs) last.to = day;
    else ranges.push({ from: day, to: day });
    return ranges;
  }, []);
}

/** Author inclusive blocked ranges; only complete, valid additions change saved restrictions. */
function BlockedDates({ dates, onChange }: { dates: string[]; onChange: (dates: string[]) => void }) {
  const [draft, setDraft] = useState({ from: '', to: '' });
  const errorId = useId();
  const start = calendarDay(draft.from);
  const end = calendarDay(draft.to);
  const count = (end - start) / calendarDayMs + 1;
  const complete = Boolean(draft.from && draft.to);
  const additions = complete && count >= 1 && count <= 366
    ? Array.from({ length: count }, (_, index) => new Date(start + index * calendarDayMs).toISOString().slice(0, 10)) : [];
  const next = [...new Set([...dates, ...additions])].sort();
  const error = !complete ? ''
    : !Number.isFinite(count) ? 'Choose a valid date.'
    : count < 1 ? 'Choose an end date on or after the start.'
    : count > 366 || next.length > 366 ? 'Choose a shorter range. You can block up to 366 dates.'
    : next.length === new Set(dates).size ? 'These dates are already blocked.' : '';
  return <div className="min-w-0">
    {blockedRanges(dates).map(({ from, to }) => <div key={from} className="mb-2 flex items-center justify-between gap-2 text-sm">
      <span className="min-w-0">
        <time dateTime={from}>{blockedDateFormat.format(new Date(`${from}T00:00:00Z`))}</time>
        {to !== from ? <> to <time dateTime={to}>{blockedDateFormat.format(new Date(`${to}T00:00:00Z`))}</time></> : null}
      </span>
      <button type="button" className={`${action} shrink-0`} aria-label={from === to ? `Remove blocked date ${from}` : `Remove blocked dates ${from} to ${to}`}
        onClick={() => onChange(dates.filter(day => day < from || day > to))}>
        <IconMinus size={16} aria-hidden="true" />
      </button>
    </div>)}
    <div className="flex flex-wrap items-end gap-2">
      <label className="min-w-0 flex-1 basis-36 space-y-1 text-sm text-muted-foreground">From
        <input type="date" aria-label="Blocked dates from" value={draft.from} min="0001-01-01" max={draft.to || '9999-12-31'}
          aria-invalid={Boolean(error)} aria-describedby={error ? errorId : undefined}
          onChange={event => { const from = event.target.value; setDraft({ from, to: !draft.to || draft.to === draft.from ? from : draft.to }); }}
          className={`${control} dark:[color-scheme:dark]`} />
      </label>
      <label className="min-w-0 flex-1 basis-36 space-y-1 text-sm text-muted-foreground">To
        <input type="date" aria-label="Blocked dates to" value={draft.to} min={draft.from || '0001-01-01'} max="9999-12-31"
          aria-invalid={Boolean(error)} aria-describedby={error ? errorId : undefined}
          onChange={event => setDraft({ ...draft, to: event.target.value })} className={`${control} dark:[color-scheme:dark]`} />
      </label>
      <button type="button" className={action} aria-label="Add blocked dates" disabled={!complete || Boolean(error)}
        onClick={() => { if (complete && !error) { onChange(next); setDraft({ from: '', to: '' }); } }}>
        <IconPlus size={16} aria-hidden="true" />
      </button>
    </div>
    <div id={errorId} aria-live="polite" aria-atomic="true">
      <AnimatedPresence show={Boolean(error)}>
        <div className="pt-2 text-xs text-danger"><AnimatedLabel text={error} multiline /></div>
      </AnimatedPresence>
    </div>
  </div>;
}

/** One inclusive limit, whether fixed, relative to today, or based on an earlier answer. */
function DateLimit({ edge, config, sources, onChange }: {
  edge: 'min' | 'max'; config: DateTimeQuestionConfig; sources: readonly FormField[];
  onChange: (config: DateTimeQuestionConfig) => void;
}) {
  const id = useId();
  const [emptyFixed, setEmptyFixed] = useState(false);
  const label = edge === 'min' ? 'Earliest' : 'Latest';
  const fixed = config[edge];
  const bound = config.rules?.[edge];
  useEffect(() => { if (fixed || bound) setEmptyFixed(false); }, [fixed, bound]);
  const source = emptyFixed ? 'fixed' : bound?.source === 'answer' ? `answer:${bound.fieldId}` : bound?.source ?? (fixed ? 'fixed' : '');
  const inputType = config.mode === 'date_time' ? 'datetime-local' : config.mode;
  /** Selecting a new source replaces this limit only; other constraints retain their values. */
  const changeSource = (value: string) => {
    setEmptyFixed(value === 'fixed');
    const next: DateRuleBound | undefined = value === 'today' ? { source: 'today', offsetDays: 0 }
      : value.startsWith('answer:') ? { source: 'answer', fieldId: value.slice(7), offsetDays: 0 } : undefined;
    onChange({ ...config, [edge]: undefined,
      ...(next || config.rules ? { rules: { ...(config.rules ?? { timezone: 'UTC' }), [edge]: next } } : {}) });
  };
  /** Fixed inputs retain exact time precision and do not retain a replaced relative limit. */
  const fixedInput = (additional = false) => <input aria-label={`${label} value`} type={inputType}
    step={config.mode === 'date' ? undefined : 60} value={fixed ?? ''} className={`${control} dark:[color-scheme:dark]`}
    min={edge === 'max' ? config.min : undefined} max={edge === 'min' ? config.max : undefined}
    onChange={event => onChange({ ...config, [edge]: event.target.value || undefined,
      ...(!additional && bound ? { rules: { ...config.rules!, [edge]: undefined } } : {}) })} />;
  return <div className="grid min-w-0 grid-cols-[4.5rem_minmax(0,1fr)] items-start gap-x-3 gap-y-2">
    <label htmlFor={id} className="py-2.5 text-sm text-muted-foreground">{label}</label>
    <div className="min-w-0 space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        <select id={id} aria-label={`${label} date`} className={`${control} flex-1 basis-36 cursor-pointer`}
          value={source} onChange={event => changeSource(event.target.value)}>
          <option value="">No limit</option><option value="fixed">{config.mode === 'time' ? 'Specific time' : 'Specific date'}</option>
          {config.mode !== 'time' ? <option value="today">Today</option> : null}
          {bound?.source === 'answer' && !sources.some(field => field.id === bound.fieldId)
            ? <option value={`answer:${bound.fieldId}`}>Choose earlier answer</option> : null}
          {config.mode !== 'time' && sources.length ? <optgroup label="Earlier answers">
            {sources.map(field => <option key={field.id} value={`answer:${field.id}`}>{field.label || 'Untitled date'}</option>)}
          </optgroup> : null}
        </select>
        {bound && source !== 'fixed' ? <div className="flex items-center gap-2 text-sm text-muted-foreground">
          {bound.offsetDays >= 0 ? <span aria-hidden="true">+</span> : null}
          <div className="w-20"><DayOffset label={`${label} offset days`} value={bound.offsetDays}
            onChange={offsetDays => onChange({ ...config, rules: { ...config.rules!, [edge]: { ...bound, offsetDays } } })} /></div>
          <span>days</span>
        </div> : null}
      </div>
      {source === 'fixed' ? fixedInput() : null}
      {bound && fixed && source !== 'fixed' ? <details className="text-xs text-muted-foreground">
        <summary className="cursor-pointer py-1">Additional fixed limit</summary>
        <div className="mt-2">{fixedInput(true)}</div>
      </details> : null}
    </div>
  </div>;
}

/** Keep common date settings visible, with blocked dates and timezone editing disclosed on demand. */
export function DateRulesEditor({ config, earlierFields = [], onChange }: {
  config: DateTimeQuestionConfig; earlierFields?: readonly FormField[]; onChange: (config: DateTimeQuestionConfig) => void;
}) {
  const timezoneList = useId();
  const blockedDatesId = useId();
  const timezoneId = useId();
  const [blockedDatesOpen, setBlockedDatesOpen] = useState(false);
  const [timezoneOpen, setTimezoneOpen] = useState(false);
  const current: DateQuestionRules = config.rules ?? { timezone: 'UTC' };
  const patch = (update: Partial<DateQuestionRules>) => onChange({ ...config, rules: { ...current, ...update } });
  const days = current.allowedWeekdays ?? [0, 1, 2, 3, 4, 5, 6];
  const sources = earlierFields.filter(field => field.type === 'date_time' && field.dateTime.mode !== 'time');
  const usesToday = current.min?.source === 'today' || current.max?.source === 'today';
  const blockedCount = current.excludedDates?.length ?? 0;
  return <div className="space-y-4">
    {config.mode !== 'time' ? <fieldset>
      <legend className="mb-2 text-sm text-muted-foreground">Allowed days</legend>
      <div className="grid max-w-80 grid-cols-7 gap-1">
        {[1, 2, 3, 4, 5, 6, 0].map(day => <button key={day} type="button" aria-label={weekdays[day]} title={weekdays[day]} aria-pressed={days.includes(day)}
          disabled={days.length === 1 && days.includes(day)}
          className={`min-h-10 min-w-0 cursor-pointer rounded-lg border text-xs transition-colors motion-reduce:transition-none hover:border-volt focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-volt disabled:cursor-default ${days.includes(day) ? 'border-volt/60 text-foreground bg-volt/10' : 'border-border text-muted-foreground'}`}
          onClick={() => patch({ allowedWeekdays: days.includes(day) ? days.filter(item => item !== day) : [...days, day].sort() })}>{weekdays[day].slice(0, 3)}</button>)}
      </div>
    </fieldset> : null}
    <div className="space-y-3">
      <DateLimit edge="min" config={config} sources={sources} onChange={onChange} />
      <DateLimit edge="max" config={config} sources={sources} onChange={onChange} />
    </div>
    {usesToday && config.mode !== 'time' ? <div>
      <button type="button" aria-expanded={timezoneOpen} aria-controls={timezoneId}
        className="inline-flex cursor-pointer items-center gap-1 rounded text-xs text-muted-foreground hover:text-foreground focus-visible:outline-2 focus-visible:outline-volt"
        onClick={() => setTimezoneOpen(!timezoneOpen)}><AnimatedLabel text={`Today uses ${current.timezone}`} /><IconChevronDown size={12} aria-hidden="true" className={`transition-transform duration-200 motion-reduce:transition-none ${timezoneOpen ? 'rotate-180' : ''}`} /></button>
      <AnimatedPresence show={timezoneOpen} id={timezoneId}><div className="pt-2">
        <input aria-label="Today timezone" list={timezoneList} value={current.timezone} className={control} spellCheck={false} autoComplete="off" onChange={event => patch({ timezone: event.target.value })} />
        <datalist id={timezoneList}>{['UTC', ...Intl.supportedValuesOf('timeZone')].map(zone => <option key={zone} value={zone} />)}</datalist>
      </div></AnimatedPresence>
    </div> : null}
    {config.mode !== 'time' ? <div className="border-t border-border/60 pt-2">
      <button type="button" className={`${action} -ml-3 gap-1.5`} aria-expanded={blockedDatesOpen} aria-controls={blockedDatesId}
        onClick={() => setBlockedDatesOpen(!blockedDatesOpen)}>
        <IconChevronRight size={14} aria-hidden="true" className={`transition-transform duration-200 motion-reduce:transition-none ${blockedDatesOpen ? 'rotate-90' : ''}`} />
        <AnimatedLabel text={blockedCount ? `Blocked dates · ${blockedCount}` : 'Blocked dates'} />
      </button>
      <AnimatedPresence show={blockedDatesOpen} id={blockedDatesId}><div className="pt-2">
        <BlockedDates dates={current.excludedDates ?? []} onChange={excludedDates => patch({ excludedDates })} />
      </div></AnimatedPresence>
    </div> : null}
  </div>;
}
